import React from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import slugify from "slugify";

function defaultSortFunction(a, b) {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
};

function toSortedPolyfill( compareFunction ) {
    const sortedArray = this.slice();
    sortedArray.sort( compareFunction || defaultSortFunction );
    return sortedArray;
};

if( !Array.prototype.toSorted ) {
    Array.prototype.toSorted = toSortedPolyfill;
}

export const copy = text =>
    navigator.clipboard.writeText(text);

export const getKey = entry =>
    slugify(entry.names[0] || "").toLowerCase();

export const formatDateString = dateString =>
    (new Date(dateString)).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

export const PillButton = styled.button`
    background: var(--fg);
    color: var(--bg);
    border-radius: 1.5rem;
    padding: 0.6rem 1.4rem;
    font-size: 0.85rem;
    transition: background 0.12s, color 0.12s;
    white-space: pre-wrap;
    
    ${ props => !props.disabled && css`
        &:hover {
            transform: translateY(-0.1rem) scale(1.01);
        }
    ` }

    ${ props => props.$outline && css`
        background: var(--bg-light);
        color: var(--fg);
    ` }
    ${ props => props.disabled && css`
        cursor: not-allowed;
        opacity: 0.5;
    ` }
    ${ props => props.$small && css`
        font-size: 0.7rem;
        padding: .24rem .8rem;
    ` }
`;

export const Stack = styled.div`
    display: grid;
    gap: 1rem;
    grid-template-columns: 100%;
    ${ props => css`gap: ${props.gap}` }
`;

export const Row = styled.div`
    display: grid;
    gap: 1rem;
    align-items: center;
    grid-auto-flow: column;
    width: max-content;
    ${ props => css`gap: ${props.gap}` }
`;

export const FlexRow = styled.div`
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-wrap: wrap;
    ${ props => css`gap: ${props.gap}` }
`;

export const Card = styled(Stack)`
    padding: 1rem;
    border-radius: 1.5rem;
    background: var(--accent-darker);
`;


export const WrappingPre = styled.pre`
    white-space: pre-wrap;
    word-wrap: break-word;
    word-wrap: anywhere;
`;

export const CodeBlock = styled.pre`
    background: var(--bg-light);
    border-radius: 0.5rem;
    padding: 0.6rem;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    overflow-wrap: anywhere;
`;

export const ForceWrap = styled.div`
    max-width: 100%;
    overflow-wrap: break-word;
    overflow-wrap: anywhere;
    overflow: scroll;
`;

const CentererOuter = styled.div`
    display: grid;
    place-items: center;
    width: 100%;
`;

const IconInner = styled.img`
    height: 2rem;
    ${ props => css`height: ${props.height}` }
`;
export const Icon = props =>
    <IconInner
        src={`/icons/${props.i}.svg`}
        {...props} />

export const Centerer = ({ children, style }) =>
    <CentererOuter style={style}>
        <Stack style={{
            padding: "2rem", maxWidth: "50rem", width: "100%",
        }}>
            { children }
        </Stack>
    </CentererOuter>;

export const Header = () =>
    <Row style={{
        width: "calc(100% + 2rem)",
        gridTemplateColumns: "max-content 1fr max-content",
        margin: "0 -1rem",
        marginBottom: "1rem",
        position: "sticky",
        top: "2rem",
        padding: "1rem 1.5rem",
        borderRadius: "5rem",
        background: "var(--fg)",
        zIndex: 1,
        gap: "0.6rem",
    }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
            <h2 style={{ color: "var(--bg)", whiteSpace: "nowrap" }}> boikot &nbsp;🙅‍♀️ </h2>
        </Link>
        <p style={{ color: "var(--bg)", fontSize: "0.85rem", textAlign: "start"}}> {window.matchMedia?.("(width > 38rem)")?.matches && "transparent and accessible corporate ethics data" } </p>
        <MenuButton />
    </Row>;

const ScreenFiller = styled.div`
    position: fixed;
    width: 100%;
    height: 100vh;
    top: 0;
    left: 0;
    right: 0;
    overflow: scroll;
`;

const BigLink = styled(Link)`
    font-size: 1.5rem;
    text-decoration: none;
    white-space: pre-wrap;
    font-weight: 400;
`;

function MenuLink({
    to,
    children,
    style,
}) {
    if( location.pathname === to )
        style = { ...style, color: "var(--fg)", fontWeight: "600" };
    return <BigLink to={to} style={style}>
        { children }
    </BigLink>;
}

function Menu({ open, close }) {
    if( !open ) return null;

    return createPortal(<ScreenFiller style={{
        backdropFilter: "blur(.4rem)",
        WebkitBackdropFilter: "blur(.4rem)",
        background: "var(--bg-transparent)",
        zIndex: 1,
    }}>
        <Centerer>
            <Row style={{
                width: "calc(100% + 2rem)",
                gridTemplateColumns: "max-content 1fr max-content",
                margin: "0 -1rem",
                marginBottom: "1rem",
                position: "sticky",
                top: "2rem",
                padding: "1rem 1.5rem",
                borderRadius: "5rem",
                background: "var(--fg)",
                zIndex: 1,
                gap: "0.6rem",
            }}>
                <Link to="/" style={{ textDecoration: 'none' }}>
                    <h2 style={{ color: "var(--bg-light)", whiteSpace: "nowrap" }}> boikot &nbsp;🙅‍♀️ </h2>
                </Link>
                <p style={{ color: "var(--bg)", fontSize: "0.85rem", textAlign: "start"}}> {window.matchMedia?.("(width > 38rem)")?.matches && "transparent and accessible corporate ethics data" } </p>
                <IconButton i="x" onClick={close} alt="close menu" style={{ filter: "invert(1)" }} />
            </Row>
            <span style={{ fontSize: "1.5rem" }}> 🏠  <MenuLink to="/"> home </MenuLink></span>
            <span style={{ fontSize: "1.5rem" }}> ⚖️  <MenuLink to="/companies"> company ethics reports </MenuLink></span>
            <span style={{ fontSize: "1.5rem" }}> ➕  <MenuLink to="/companies/edit"> add a company </MenuLink></span>
            <span style={{ fontSize: "1.5rem" }}> 🖨️  <MenuLink to="/statement-score"> scan a document </MenuLink></span>
            {/* <MenuLink to="/search"> 🔎  search </MenuLink> */}
            {/* <MenuLink to="/blog"> blog </MenuLink> */}
            <span style={{ fontSize: "1.5rem" }}> 📬  <MenuLink to="/contact-us"> contact us </MenuLink></span>
            <h3 style={{ marginTop: "1.5rem" }}>
                boikot makes company ethics data transparent and accessible.
            </h3>
            <p style={{ marginBottom: "1.5rem" }}>
                We are building a community-curated, transparent, freely
                accessible collection of corporate ethics records. By
                documenting ethical and unethical business practices,
                we aim to inform consumer choice, raise the cost of
                harmful business decisions, and incentivise companies to
                act responsibly in the public interest. </p>
        </Centerer>
    </ScreenFiller>, document.getElementById("modal-portal"));
}

export function IconButton( props ) {
    return <button onClick={ props.onClick }>
        <Row gap={props.gap}>
            <Icon {...{...props, onClick: undefined, children: undefined}} />
            { props.children }
        </Row>
    </button>;
}

export function MenuButton() {
    const [ open, setOpen ] = React.useState(false);

    React.useEffect( () => {
        window.addEventListener("keydown", e => e.key === "Escape" && setOpen(false));
    }, [] );

    return <>
        <IconButton i="menu" alt="open menu"
            style={{ filter: "invert(1)" }}
            onClick={ () => setOpen(true) } />
        <Menu open={open} close={ () => setOpen(false) } />
    </>;
}

export const VersionNumber = () =>
    <span> {window.location.host} @ { process.env.VERSION } </span>;

export const Badge = styled.span`
    background: var(--accent-dark);
    border-radius: 1.2rem;
    font-size: 0.85rem;
    font-weight: 600;
    padding: 0.3rem 0.6rem;
    color: var(--fg);
    ${ props => props.$inactive && css`
        background: transparent;
    ` }
`;

export function TagBadge({ children, ...props }) {
    return <Link to={`/companies?tag=${children}`}>
        <Badge {...props}>
            { children }
        </Badge>
    </Link>;
}

export function IconBadge({ children, i, flip, ...props }) {
    return <Badge {...props} style={{ display: "grid" }}>
        <Row gap="0.2rem">
            { flip && children }
            <Icon i={i} style={{ height: "0.9rem" }}/>
            { !flip && children }
        </Row>
    </Badge>;
}

let reorderGracePeriod = false;
function setReorderGracePeriod() {
    reorderGracePeriod = true;
    setTimeout( () => ( reorderGracePeriod = false ), 50 );
}
function reorderItems(items, src, tgt) {
    if( src === tgt || src === -1 || tgt === -1 || reorderGracePeriod)
        return items;
    if( src < tgt )
        return [...items.slice(0, src), ...items.slice(src+1,tgt+1), items[src], ...items.slice(tgt+1)];
    if( src > tgt )
        return [...items.slice(0, tgt), items[src], ...items.slice(tgt,src), ...items.slice(src+1)];
}

export function DeleteableBadgeList({ items, update, deleteAtIndex = () => {} }) {
    const [dragging, setDragging] = React.useState(null);
    if( !items?.length ) return null;
    return <FlexRow>
        { items.map( (item, i) =>
            <button
                onClick={() => deleteAtIndex(i)} key={item}
                onDragEnter={e => e.preventDefault() + update(reorderItems(items, items.indexOf(dragging), items.indexOf(e.target.textContent))) + setReorderGracePeriod()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => e.preventDefault()}
                onDragStart={e => setDragging(item)}
                onDragEnd={e => e.preventDefault()}
                draggable>
                <div style={{ pointerEvents: "none" }}>
                    <IconBadge i="x" flip onDrop={e => e.preventDefault()}>
                        { item }
                    </IconBadge>
                </div>
            </button>
        ) }
    </FlexRow>;
}

export function Page({ children, style }) {
    React.useEffect(
        () => { window.scrollTo(0, 0); }, []
    );
    return <CentererOuter style={{ minHeight: "100vh" }}>
        <Stack style={{
            padding: "2rem 2rem 1rem 2rem",
            width: "100%",
            maxWidth: "50rem",
            minHeight: "100vh",
            gridTemplateRows: "min-content 1fr min-content",
            gridTemplateColumns: "100%",
            ...style,
        }}>
            <Header />
            <div> { children } </div>
            <Footer />
        </Stack>
    </CentererOuter>;
}

export const isEmail = email => email.match( /.+@.+\..+/ );

function MailingList() {
    const [ email, setEmail ] = React.useState("");
    const borderColour = !!email.length && !isEmail(email) ? "red" : "var(--bg)";
    return <form name="mailing-list" method="post">
        <input type="hidden" name="form-name" value="mailing-list" />
        <Stack>
            <p> Enter your email to join our mailing list
                and stay up to date! </p>
            <Row style={{
                width: "100%", gridTemplateColumns: "1fr max-content"
            }}>
                <input type="email" name="email"
                    placeholder="enter your email" value={ email }
                    style={{
                        border: `1px solid ${borderColour}`,
                        minWidth: 0
                    }}
                    onChange={ e => setEmail(e.target.value) }/>
                <PillButton type="submit" disabled={ !isEmail(email) }>
                    submit  📨
                </PillButton>
            </Row>
        </Stack>
    </form>;
}

export function Footer() {
    React.useLayoutEffect( () => { window.renderCallback?.(); }, [] );
    const footerLinkStyle = { fontSize: "1rem", whiteSpace: "unset", textDecoration: "revert" };
    return <Stack style={{ marginTop: "3rem", fontSize: "0.9rem" }}>
        <hr />
        <Stack>
            <p style={{ opacity: 0.6 }}> Keep Companies Accountable · Tax Wealth not Work · Boycott, Divestment and Sanctions </p>
            <span style={{ height: ".5rem" }} />
            <MailingList />
            <span style={{ height: ".5rem" }} />
            <FlexRow style={{ gap: "0.75rem 2rem" }}>
                <MenuLink to="/companies" style={footerLinkStyle}>
                    company ethics reports
                </MenuLink>
                <MenuLink to="/contact-us" style={footerLinkStyle}> contact us </MenuLink>
                <MenuLink to="/privacy-policy" style={footerLinkStyle}> privacy policy </MenuLink>
                <MenuLink to="/terms-and-conditions" style={footerLinkStyle}>
                    terms and conditions
                </MenuLink>
            </FlexRow>
            <FlexRow>
                <a href="https://github.com/boikot-xyz/boikot">
                    <Icon i="github" height="1.75rem" alt="boikot github" />
                </a>
                <VersionNumber />
            </FlexRow>
        </Stack>
    </Stack>;
}



export const PopperOuter = styled.div`
    & .popper-inner {
        display: none;
    }

    &:focus-within .popper-inner {
        display: unset;
    }
`;

export function PillPopper({ buttonContent, children, ...props }) {
    return <PopperOuter style={{ position: "relative" }}>
        <PillButton {...props}>{ buttonContent }</PillButton>
        <div class="popper-inner"> { children } </div>
    </PopperOuter>;
}

export function ResizingInput({ ...props }) {
    return <div style={{ display: "grid", gridTemplateColumns: "min-content", ...props.style }}>
        <span style={{ gridColumn: 1, gridRow: 1, padding: "0.6rem", fontSize: "0.9rem", whiteSpace: "pre" }}>
            { props?.value || "" }
        </span>
        <span style={{ gridColumn: 1, gridRow: 1, padding: "0.6rem", fontSize: "0.9rem", whiteSpace: "pre" }}>
            { props?.placeholder || "" }
        </span>
        <input {...props} style={{ gridColumn: 1, gridRow: 1, minWidth: 0, width: "100%" }} />
    </div>;
}
